/* eslint-disable sort-keys */
import React, { useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import {
  Box,
  IconButton,
  Radio,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { DataService } from 'api/DataService'
import moment, { Moment } from 'moment'

import Can from 'components/Can'
import IconContainer from 'components/IconContainer/IconContainer'
import DatePicker, { DateProps } from 'components/v2/DatePicker'

import { theme } from 'styles/theme'

import * as S from '../../styles'

type DateFilterType = 'date_paid' | 'expected_on'

type BetweenDate = {
  start: Moment | null
  end: Moment | null
}

type FinancialStatementFormProps = {
  // SC-10999: seletor "Empresa" na visão multi-empresas. O extrato é de uma
  // empresa (saldo, custos e transferências dela)
  companySelector?: React.ReactNode
}

const FinancialStatementForm: React.FC<FinancialStatementFormProps> = ({
  companySelector
}) => {
  const [loading, setLoading] = useState(false)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false)
  const [dateFilterType, setDateFilterType] =
    useState<DateFilterType>('date_paid')
  const [betweenDate, setBetweenDate] = useState<BetweenDate>({
    start: null,
    end: null
  })
  const [tempDate, setTempDate] = useState<BetweenDate>({
    start: null,
    end: null
  })
  const [tempFilterType, setTempFilterType] =
    useState<DateFilterType>('date_paid')

  const handleToggleModal = () => {
    if (!isOpenModal) {
      setTempDate({ ...betweenDate })
      setTempFilterType(dateFilterType)
    }
    setIsOpenModal((s) => !s)
  }

  const handleCancel = () => {
    setTempDate({ ...betweenDate })
    setTempFilterType(dateFilterType)
    setIsOpenModal(false)
  }

  const handleApply = () => {
    setBetweenDate({ ...tempDate })
    setDateFilterType(tempFilterType)
    setIsOpenModal(false)
  }

  const onChangeDate = ({ value }: { value: DateProps }) => {
    if (!value) {
      setTempDate({ start: null, end: null })
    } else if (!Array.isArray(value)) {
      setTempDate({
        start: moment(value),
        end: moment(value)
      })
    } else {
      const [start, end] = value
      setTempDate({
        start: start ? moment(start) : null,
        end: end ? moment(end) : start ? moment(start) : null
      })
    }
  }

  const applyPresetDate = (startDate: string, endDate: string) => {
    setBetweenDate({
      start: moment(startDate),
      end: moment(endDate)
    })
    setDateFilterType(tempFilterType)
    setIsOpenModal(false)
  }

  const getToday = () => {
    const day = moment().format('YYYY-MM-DD')
    applyPresetDate(day, day)
  }

  const getYesterday = () => {
    const day = moment().subtract(1, 'days').format('YYYY-MM-DD')
    applyPresetDate(day, day)
  }

  const getLastDays = (days: number) => {
    const initialDate = moment().subtract(days, 'days').format('YYYY-MM-DD')
    const endDate = moment().format('YYYY-MM-DD')
    applyPresetDate(initialDate, endDate)
  }

  const getMonth = () => {
    const initialDate = moment().startOf('month').format('YYYY-MM-DD')
    const endDate = moment().endOf('month').format('YYYY-MM-DD')
    applyPresetDate(initialDate, endDate)
  }

  const getPreviousMonth = () => {
    const initialDate = moment()
      .subtract(1, 'months')
      .startOf('month')
      .format('YYYY-MM-DD')
    const endDate = moment()
      .subtract(1, 'months')
      .endOf('month')
      .format('YYYY-MM-DD')
    applyPresetDate(initialDate, endDate)
  }

  const getLastMonths = (months: number) => {
    const initialDate = moment().subtract(months, 'months').format('YYYY-MM-DD')
    const endDate = moment().format('YYYY-MM-DD')
    applyPresetDate(initialDate, endDate)
  }

  const formatDateLabel = () => {
    if (!betweenDate.start || !betweenDate.end) return 'Selecionar período'

    const startFormatted = betweenDate.start.format('DD/MM/YYYY')
    const endFormatted = betweenDate.end.format('DD/MM/YYYY')

    if (startFormatted === endFormatted) {
      return startFormatted
    }

    return `${startFormatted} - ${endFormatted}`
  }

  const handleClearDate = () => {
    setBetweenDate({ start: null, end: null })
  }

  const hasSelectedDate = !!betweenDate.start && !!betweenDate.end
  const canApplyFilter = !!tempDate.start

  const handleOpenConfirmDialog = () => {
    if (!hasSelectedDate) {
      toast.error('Selecione um período para gerar o relatório')
      return
    }
    setIsOpenConfirmDialog(true)
  }

  const handleGenerateReport = async () => {
    setIsOpenConfirmDialog(false)
    setLoading(true)
    const id = toast.loading('Solicitando relatório...')

    const model =
      dateFilterType === 'date_paid'
        ? 'StatementDatePaid'
        : 'StatementExpectedOn'

    const filterKey =
      dateFilterType === 'date_paid'
        ? 'date_paid_between'
        : 'expected_on_between'

    const startDate = betweenDate.start?.format('YYYY-MM-DD') ?? ''
    const endDate = betweenDate.end?.format('YYYY-MM-DD') ?? ''

    const data = {
      model,
      filter_by: `${filterKey}=${startDate}|${endDate}`
    }

    try {
      const response = await DataService({
        data,
        type: 'POST',
        url: '/v1/reports'
      })

      const errorMessage =
        response.error && (response.detail_error || response.message)

      toast.update(id, {
        render: response.error
          ? errorMessage
          : 'Relatório solicitado com sucesso! Ele aparecerá na aba "Relatórios emitidos" quando estiver pronto.',
        type: response.error ? 'error' : 'success',
        isLoading: false,
        autoClose: 5000,
        closeButton: true
      })
    } catch (error) {
      toast.update(id, {
        render: 'Erro ao solicitar relatório',
        type: 'error',
        isLoading: false,
        autoClose: 5000,
        closeButton: true
      })
    }

    setLoading(false)
  }

  return (
    <>
      {companySelector}
      <S.SectionDescription>
        <Typography variant="smLight">
          Gere um extrato detalhado com cobranças recebidas, custos de uso e
          transferências realizadas.
        </Typography>
      </S.SectionDescription>

      <S.FormRow>
        <S.FormField>
          <Box display="flex" alignItems="center" gap={1} mt={2}>
            <S.FormLabel>Período e tipo de filtro</S.FormLabel>
            <Tooltip
              enterTouchDelay={0}
              arrow
              placement="right"
              title="Data de pagamento: É a data em que o cliente efetuou o
        pagamento da fatura. Data de liberação: É a data em que o valor foi liberado no
        seu saldo."
            >
              <Box display={'flex'} justifyContent={'top'}>
                <Icon
                  icon="info"
                  sx={{
                    alignSelf: 'end',
                    marginTop: '15%',
                    fontSize: '1.2rem',
                    color: theme.palette.neutral[700]
                  }}
                />
              </Box>
            </Tooltip>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            justifyContent="flex-start"
          >
            <S.DatePickerButton onClick={handleToggleModal}>
              <Icon icon="event" sx={{ color: theme.palette.neutral[400] }} />
              <Typography
                variant="smLight"
                sx={{
                  color: hasSelectedDate
                    ? theme.palette.neutral[200]
                    : theme.palette.neutral[500]
                }}
              >
                {formatDateLabel()}
              </Typography>
              {hasSelectedDate && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClearDate()
                  }}
                  sx={{ ml: 'auto', p: 0.5 }}
                >
                  <Icon
                    icon="close"
                    sx={{ fontSize: '16px', color: theme.palette.neutral[400] }}
                  />
                </IconButton>
              )}
              {!hasSelectedDate && (
                <Icon
                  icon="arrowDropDown"
                  sx={{ color: theme.palette.neutral[400], marginLeft: 'auto' }}
                />
              )}
            </S.DatePickerButton>

            <Can permission="reports:export">
              <Button
                variantButton="primary"
                onClick={handleOpenConfirmDialog}
                disabled={loading || !hasSelectedDate}
              >
                {loading ? 'Gerando...' : 'Gerar Extrato'}
              </Button>
            </Can>
          </Box>
        </S.FormField>
      </S.FormRow>

      <Modal
        open={isOpenModal}
        handleClose={handleToggleModal}
        width="fit-content"
      >
        <ModalBody>
          <ModalHeader onClose={handleToggleModal}>
            <Stack direction="row" alignItems="center" gap={2}>
              <IconContainer icon="watchLater" />
              <Typography variant="mdBold">Filtrar período</Typography>
            </Stack>
          </ModalHeader>

          <ModalContent sx={{ mb: 4 }}>
            <S.FilterModalContent>
              <S.FilterColumn>
                <Typography fontSize="14px" variant="smBold">
                  Filtrar por
                </Typography>
                <S.FilterOptionsWrapper sx={{ gap: '15px' }}>
                  <S.FilterOption>
                    <Radio
                      sx={{ mt: '-13px', ml: '-10px' }}
                      checked={tempFilterType === 'date_paid'}
                      onClick={() => setTempFilterType('date_paid')}
                    />
                    <Typography variant="smLight">Data de pagamento</Typography>
                  </S.FilterOption>
                  <S.FilterOption>
                    <Radio
                      sx={{ mt: '-13px', ml: '-10px' }}
                      checked={tempFilterType === 'expected_on'}
                      onClick={() => setTempFilterType('expected_on')}
                    />
                    <Typography variant="smLight">Data de liberação</Typography>
                  </S.FilterOption>
                </S.FilterOptionsWrapper>
              </S.FilterColumn>

              <S.FilterColumn>
                <Typography fontSize="14px" variant="smBold">
                  Período
                </Typography>

                <S.FilterOptionsWrapper sx={{ gap: '4px' }}>
                  <S.PresetPeriodItem onClick={getToday}>
                    <Typography variant="smLight">Hoje</Typography>
                  </S.PresetPeriodItem>
                  <S.PresetPeriodItem onClick={getYesterday}>
                    <Typography variant="smLight">Ontem</Typography>
                  </S.PresetPeriodItem>
                  <S.PresetPeriodItem onClick={() => getLastDays(7)}>
                    <Typography variant="smLight">Últimos 7 dias</Typography>
                  </S.PresetPeriodItem>
                  <S.PresetPeriodItem onClick={() => getLastDays(30)}>
                    <Typography variant="smLight">Últimos 30 dias</Typography>
                  </S.PresetPeriodItem>
                  <S.PresetPeriodItem onClick={getMonth}>
                    <Typography variant="smLight">Este mês</Typography>
                  </S.PresetPeriodItem>
                  <S.PresetPeriodItem onClick={getPreviousMonth}>
                    <Typography variant="smLight">Mês passado</Typography>
                  </S.PresetPeriodItem>
                  <S.PresetPeriodItem onClick={() => getLastMonths(3)}>
                    <Typography variant="smLight">Últimos 3 meses</Typography>
                  </S.PresetPeriodItem>
                  <S.PresetPeriodItem onClick={() => getLastMonths(12)}>
                    <Typography variant="smLight">Últimos 12 meses</Typography>
                  </S.PresetPeriodItem>
                </S.FilterOptionsWrapper>
              </S.FilterColumn>

              <S.FilterColumn>
                <Box
                  height="20.50rem"
                  width="18.25rem"
                  display="flex"
                  sx={{ justifyContent: 'center' }}
                >
                  <DatePicker
                    filterKey="date_paid_between"
                    initialDate={tempDate.start?.toDate() || null}
                    lastDate={tempDate.end?.toDate() || null}
                    handleDeleteFilters={() => ({})}
                    onChangeDate={onChangeDate}
                    triggered={false}
                    setTriggered={() => undefined}
                  />
                </Box>
              </S.FilterColumn>
            </S.FilterModalContent>
          </ModalContent>

          <ModalFooter>
            <Button variantButton="neutral" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button
              variantButton="primary"
              onClick={handleApply}
              disabled={!canApplyFilter}
            >
              Aplicar
            </Button>
          </ModalFooter>
        </ModalBody>
      </Modal>

      <Dialog
        icon="assignment"
        title="Relatório"
        description="Confirma exportar o relatório?"
        setIsOpenDialog={setIsOpenConfirmDialog}
        isOpenDialog={isOpenConfirmDialog}
        cancelButton
        fullWidth
        maxWidth="xs"
      >
        <Button
          disabled={loading}
          onClick={handleGenerateReport}
          fullWidth
          loading={loading}
        >
          Confirmar
        </Button>
      </Dialog>
    </>
  )
}

export default FinancialStatementForm
